'use client';

import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { usePathname } from 'next/navigation';

import { isRouteActive } from '@kit/ui/utils';

import { adminNavigationGroups, type AdminNavGroup } from './admin-nav-data';

// Re-export the canonical data so existing importers keep importing from
// './admin-tab-context'. The data itself now lives in ./admin-nav-data.
export {
  adminNavigationGroups,
  adminNavItems,
  adminNavItemsByPath,
} from './admin-nav-data';
export type { AdminNavItem, AdminNavGroup } from './admin-nav-data';

// Get the group that contains the current path
function getActiveGroup(pathname: string): string {
  for (const group of adminNavigationGroups) {
    for (const item of group.items) {
      if (isRouteActive(item.path, pathname, item.end)) {
        return group.label;
      }
    }
  }
  return 'Operations';
}

// Context type
interface AdminTabContextType {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  currentGroup: AdminNavGroup | undefined;
}

const AdminTabContext = createContext<AdminTabContextType | null>(null);

// Provider component
export function AdminTabProvider({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const [activeTab, setActiveTabState] = useState(() =>
    getActiveGroup(pathname),
  );

  // Update tab when navigating to a different group
  useEffect(() => {
    const newActiveGroup = getActiveGroup(pathname);
    if (newActiveGroup !== activeTab) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- sync tab state with URL navigation
      setActiveTabState(newActiveGroup);
    }
  }, [pathname, activeTab]);

  const setActiveTab = useCallback((tab: string) => {
    setActiveTabState(tab);
  }, []);

  const currentGroup = adminNavigationGroups.find((g) => g.label === activeTab);

  return (
    <AdminTabContext.Provider value={{ activeTab, setActiveTab, currentGroup }}>
      {children}
    </AdminTabContext.Provider>
  );
}

// Hook to use the context

// Optional hook that returns null if not in provider (for conditional usage)
